import { useState } from 'react'
import { LuBookOpen, LuPuzzle, LuWrench, LuLightbulb, LuFileText, LuTriangleAlert, LuCheck } from 'react-icons/lu'

export type ClueExplanation =
  | WordplayExplanation
  | DoubleDefinitionExplanation
  | AndLitExplanation
  | CrypticDefinitionExplanation
  | NoCleanParseExplanation

interface NewWordplayStep {
  tokens: string
  operation: string
  result: string
  clue_after: string
}

// Old format kept for backward compat with unregenerated rows
interface OldWordplayStep {
  indicator: string
  operation: string
  result: string
}

type WordplayStep = NewWordplayStep | OldWordplayStep

function isNewStep(step: WordplayStep): step is NewWordplayStep {
  return 'tokens' in step
}

export interface ClueToken {
  text: string
  role: 'definition' | 'wordplay' | 'indicator' | 'link'
}

export interface WordplayExplanation {
  clue_type: 'wordplay'
  clue_segmentation?: ClueToken[]
  definition: string
  letter_breakdown?: Array<{ source: string; letters: string }>
  wordplay_steps?: WordplayStep[]
  hint: {
    definition_location: 'start' | 'end'
    wordplay_types: string[]
  } | string
  full_explanation: string
}

export interface DoubleDefinitionExplanation {
  clue_type: 'double_definition'
  clue_segmentation?: ClueToken[]
  definitions: Array<{ definition: string; sense?: string }> | string[]
  hint: { definition_count: 2 } | string
  full_explanation: string
}

export interface AndLitExplanation {
  clue_type: '&lit'
  definition_scope: 'entire_clue'
  clue_segmentation?: ClueToken[]
  letter_breakdown?: Array<{ source: string; letters: string }>
  wordplay_steps?: WordplayStep[]
  hint: { wordplay_types: string[] } | string
  full_explanation: string
}

export interface CrypticDefinitionExplanation {
  clue_type: 'cryptic_definition'
  definition_scope: 'entire_clue'
  clue_segmentation?: ClueToken[]
  definition_paraphrase: string
  hint: { definition_scope: 'entire_clue' }
  full_explanation: string
}

export interface NoCleanParseExplanation {
  clue_type: 'no_clean_parse'
  intended_clue_type: 'wordplay' | 'double_definition' | '&lit' | 'cryptic_definition'
  clue_segmentation?: ClueToken[]
  definition: string
  issue: string
  hint: { intended_clue_type: 'wordplay' | 'double_definition' | '&lit' | 'cryptic_definition' }
  full_explanation: string
}

type RevealedSections = {
  definition: boolean
  wordplayTypes: boolean
  wordplaySteps: boolean
  fullExplanation: boolean
}

interface ClueExplanationDisplayProps {
  explanation: ClueExplanation
  onReport?: (feedback?: string) => void
  reportLoading?: boolean
  hasReported?: boolean
}

export function ClueExplanationDisplay({ explanation, onReport, reportLoading, hasReported }: ClueExplanationDisplayProps) {
  const [revealedSections, setRevealedSections] = useState<RevealedSections>({
    definition: false,
    wordplayTypes: false,
    wordplaySteps: true,
    fullExplanation: true,
  })

  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false)
  const [feedback, setFeedback] = useState('')

  const revealSection = (section: keyof RevealedSections) => {
    setRevealedSections((prev) => ({ ...prev, [section]: true }))
  }

  const renderWordplayExplanation = (exp: WordplayExplanation) => (
    <>
      <ExplanationSection
        title={<span className="flex items-center gap-1"><LuBookOpen size={14} /> Definition</span>}
        revealed={revealedSections.definition}
        onReveal={() => revealSection('definition')}
      >
        <p className="text-text">
          <span className="font-semibold">"{exp.definition}"</span>
          {typeof exp.hint === 'object' && exp.hint.definition_location && (
            <span className="text-text-secondary ml-2">
              (at the {exp.hint.definition_location} of the clue)
            </span>
          )}
        </p>
      </ExplanationSection>

      {typeof exp.hint === 'object' && exp.hint.wordplay_types && exp.hint.wordplay_types.length > 0 && (
        <ExplanationSection
          title={<span className="flex items-center gap-1"><LuPuzzle size={14} /> Wordplay Types</span>}
          revealed={revealedSections.wordplayTypes}
          onReveal={() => revealSection('wordplayTypes')}
        >
          <div className="flex flex-wrap gap-2">
            {exp.hint.wordplay_types.map((type, i) => (
              <span key={i} className="px-3 py-1 bg-secondary/10 text-secondary text-sm rounded-full">
                {type}
              </span>
            ))}
          </div>
        </ExplanationSection>
      )}

      {(exp.wordplay_steps && exp.wordplay_steps.length > 0
        ? true
        : exp.letter_breakdown && exp.letter_breakdown.length > 0) && (
        <ExplanationSection
          title={<span className="flex items-center gap-1"><LuWrench size={14} /> Wordplay Steps</span>}
          revealed={revealedSections.wordplaySteps}
          onReveal={() => revealSection('wordplaySteps')}
        >
          <WordplayStepsList steps={exp.wordplay_steps} letterBreakdown={exp.letter_breakdown} />
        </ExplanationSection>
      )}
    </>
  )

  const renderDoubleDefinitionExplanation = (exp: DoubleDefinitionExplanation) => (
    <>
      <ExplanationSection
        title={<span className="flex items-center gap-1"><LuBookOpen size={14} /> Two Definitions</span>}
        revealed={revealedSections.definition}
        onReveal={() => revealSection('definition')}
      >
        <div className="space-y-3">
          {(Array.isArray(exp.definitions) ? exp.definitions : []).map((def, i) => {
            const definition = typeof def === 'string' ? def : def.definition
            const sense = typeof def === 'string' ? undefined : def.sense

            return (
              <div key={i} className="flex items-start gap-3">
                <span className="text-primary font-bold text-sm mt-0.5">{i + 1}.</span>
                <div>
                  <p className="text-text font-medium">"{definition}"</p>
                  {sense && <p className="text-text-secondary text-sm">{sense}</p>}
                </div>
              </div>
            )
          })}
        </div>
      </ExplanationSection>

      <ExplanationSection
        title={<span className="flex items-center gap-1"><LuLightbulb size={14} /> Hint</span>}
        revealed={revealedSections.wordplayTypes}
        onReveal={() => revealSection('wordplayTypes')}
      >
        <p className="text-text">
          {typeof exp.hint === 'string'
            ? exp.hint
            : 'Two distinct definitions point to the same answer.'}
        </p>
      </ExplanationSection>
    </>
  )

  const renderAndLitExplanation = (exp: AndLitExplanation) => (
    <>
      <ExplanationSection
        title={<span className="flex items-center gap-1"><LuBookOpen size={14} /> Definition</span>}
        revealed={revealedSections.definition}
        onReveal={() => revealSection('definition')}
      >
        <p className="text-text">
          <span className="font-semibold">The entire clue serves as both definition and wordplay</span>
          <span className="text-text-secondary block mt-2 text-sm">
            This is an "&amp;lit" (all-in-one) clue where every word works double duty.
          </span>
        </p>
      </ExplanationSection>

      {typeof exp.hint === 'object' && exp.hint.wordplay_types && exp.hint.wordplay_types.length > 0 && (
        <ExplanationSection
          title={<span className="flex items-center gap-1"><LuPuzzle size={14} /> Wordplay Types</span>}
          revealed={revealedSections.wordplayTypes}
          onReveal={() => revealSection('wordplayTypes')}
        >
          <div className="flex flex-wrap gap-2">
            {exp.hint.wordplay_types.map((type, i) => (
              <span key={i} className="px-3 py-1 bg-secondary/10 text-secondary text-sm rounded-full">
                {type}
              </span>
            ))}
          </div>
        </ExplanationSection>
      )}

      {(exp.wordplay_steps && exp.wordplay_steps.length > 0
        ? true
        : exp.letter_breakdown && exp.letter_breakdown.length > 0) && (
        <ExplanationSection
          title={<span className="flex items-center gap-1"><LuWrench size={14} /> Wordplay Steps</span>}
          revealed={revealedSections.wordplaySteps}
          onReveal={() => revealSection('wordplaySteps')}
        >
          <WordplayStepsList steps={exp.wordplay_steps} letterBreakdown={exp.letter_breakdown} />
        </ExplanationSection>
      )}
    </>
  )

  const renderCrypticDefinitionExplanation = (exp: CrypticDefinitionExplanation) => (
    <>
      <ExplanationSection
        title={<span className="flex items-center gap-1"><LuBookOpen size={14} /> Cryptic Definition</span>}
        revealed={revealedSections.definition}
        onReveal={() => revealSection('definition')}
      >
        <div className="space-y-3">
          <p className="text-text">
            <span className="font-semibold">The entire clue is a single, misleading definition</span>
            <span className="text-text-secondary block mt-2 text-sm">
              No wordplay - just a clever, oblique way to describe the answer.
            </span>
          </p>
          <div className="border-l-4 border-primary/50 pl-3 mt-3">
            <p className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-1">
              What it's defining:
            </p>
            <p className="text-text">{exp.definition_paraphrase}</p>
          </div>
        </div>
      </ExplanationSection>
    </>
  )

  const renderNoCleanParseExplanation = (exp: NoCleanParseExplanation) => (
    <>
      <ExplanationSection
        title={<span className="flex items-center gap-1"><LuBookOpen size={14} /> Definition</span>}
        revealed={revealedSections.definition}
        onReveal={() => revealSection('definition')}
      >
        <div className="space-y-2">
          <p className="text-text">
            <span className="font-semibold">"{exp.definition}"</span>
          </p>
          <p className="text-text-secondary text-sm">
            Wordplay not shown: {exp.issue}
          </p>
        </div>
      </ExplanationSection>
    </>
  )

  return (
    <div className="flex flex-col gap-3">
      {/* Clue Segmentation Visualization */}
      {explanation.clue_segmentation && explanation.clue_segmentation.length > 0 && (
        <div className="p-4 bg-surface border border-border rounded-lg mb-2">
          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
            Clue Analysis & Word Roles
          </p>
          <div className="flex flex-wrap gap-x-1.5 gap-y-2 items-center leading-relaxed">
            {explanation.clue_segmentation.map((token, i) => {
              let roleClass = ''
              let roleName = ''
              
              switch (token.role) {
                case 'definition':
                  roleClass = 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/30'
                  roleName = 'Definition'
                  break
                case 'wordplay':
                  roleClass = 'bg-blue-500/10 text-blue-500 border border-blue-500/30 font-semibold'
                  roleName = 'Wordplay/Fodder'
                  break
                case 'indicator':
                  roleClass = 'bg-amber-500/10 text-amber-500 border border-amber-500/30 italic'
                  roleName = 'Indicator'
                  break
                case 'link':
                  roleClass = 'text-text-secondary opacity-60'
                  roleName = 'Link/Filler'
                  break
              }

              return (
                <span
                  key={i}
                  title={roleName}
                  className={`px-2 py-0.5 rounded text-sm transition-all hover:scale-105 select-none ${roleClass}`}
                >
                  {token.text}
                </span>
              )
            })}
          </div>
          
          {/* Legend/Key */}
          <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t border-border/50 text-xs">
            <span className="flex items-center gap-1.5 text-emerald-500">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 inline-block"></span>
              Definition
            </span>
            <span className="flex items-center gap-1.5 text-blue-500">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500/20 border border-blue-500/40 inline-block"></span>
              Wordplay/Fodder
            </span>
            <span className="flex items-center gap-1.5 text-amber-500">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500/20 border border-amber-500/40 inline-block"></span>
              Indicator
            </span>
            <span className="flex items-center gap-1.5 text-text-secondary opacity-60">
              Link/Filler
            </span>
          </div>
        </div>
      )}

      {/* Render sections based on clue type */}
      {explanation.clue_type === 'wordplay' &&
        renderWordplayExplanation(explanation as WordplayExplanation)}

      {explanation.clue_type === 'double_definition' &&
        renderDoubleDefinitionExplanation(explanation as DoubleDefinitionExplanation)}

      {explanation.clue_type === '&lit' &&
        renderAndLitExplanation(explanation as AndLitExplanation)}

      {explanation.clue_type === 'cryptic_definition' &&
        renderCrypticDefinitionExplanation(explanation as CrypticDefinitionExplanation)}

      {explanation.clue_type === 'no_clean_parse' &&
        renderNoCleanParseExplanation(explanation as NoCleanParseExplanation)}

      {/* Full Explanation (common to all types) */}
      <ExplanationSection
        title={<span className="flex items-center gap-1"><LuFileText size={14} /> Full Explanation</span>}
        revealed={revealedSections.fullExplanation}
        onReveal={() => revealSection('fullExplanation')}
      >
        <p className="text-text whitespace-pre-wrap leading-relaxed">
          {'full_explanation' in explanation ? explanation.full_explanation : ''}
        </p>
      </ExplanationSection>

      {/* Report Button */}
      {onReport && (
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => setShowFeedbackPopup(true)}
            disabled={reportLoading || hasReported}
            className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
              hasReported
                ? 'bg-surface text-text-secondary cursor-not-allowed'
                : 'bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-50'
            }`}
          >
            {reportLoading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                Reporting...
              </span>
            ) : hasReported ? (
              <span className="flex items-center gap-1"><LuCheck size={14} /> Reported</span>
            ) : (
              <span className="flex items-center gap-1"><LuTriangleAlert size={14} /> Report Bad Explanation</span>
            )}
          </button>
        </div>
      )}

      {/* Feedback Popup */}
      {showFeedbackPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-bg border border-border rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-text mb-3">Report Explanation Issue</h3>
            <p className="text-sm text-text-secondary mb-4">
              Please describe what's wrong with this explanation (optional):
            </p>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="e.g., The wordplay explanation is incorrect, the definition is wrong, etc."
              className="w-full h-24 px-3 py-2 bg-input-bg border border-border rounded-lg text-text placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              autoFocus
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowFeedbackPopup(false)
                  setFeedback('')
                }}
                className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onReport?.(feedback)
                  setShowFeedbackPopup(false)
                  setFeedback('')
                }}
                className="px-4 py-2 text-sm font-medium bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Parsewords-style step display
function WordplayStepsList({
  steps,
  letterBreakdown,
}: {
  steps?: WordplayStep[]
  letterBreakdown?: Array<{ source: string; letters: string }>
}) {
  if (steps && steps.length > 0) {
    return (
      <div className="space-y-3">
        {steps.map((step, i) => {
          if (isNewStep(step)) {
            return (
              <div key={i} className="flex flex-col gap-1.5">
                {/* Token → operation → result row */}
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Step number */}
                  <span className="text-xs font-bold text-text-secondary w-4 shrink-0 text-right">
                    {i + 1}.
                  </span>

                  {/* Yellow skewed token box (parsewords style) */}
                  <div
                    style={{ transform: 'skewX(-8deg)', background: '#facc15' }}
                    className="rounded px-3 py-1"
                  >
                    <span
                      style={{ display: 'inline-block', transform: 'skewX(8deg)' }}
                      className="text-sm font-medium text-black font-mono"
                    >
                      {step.tokens}
                    </span>
                  </div>

                  {/* Arrow + operation */}
                  <span className="text-text-secondary text-xs">→</span>
                  <span className="text-xs text-text-secondary italic">{step.operation}</span>
                  <span className="text-text-secondary text-xs">→</span>

                  {/* Blue result pill */}
                  <span className="px-3 py-1 rounded-full bg-blue-500 text-white text-sm font-bold tracking-widest font-mono">
                    {step.result}
                  </span>
                </div>

                {/* clue_after in muted mono */}
                <p className="text-xs text-text-secondary font-mono pl-6 truncate" title={step.clue_after}>
                  {step.clue_after}
                </p>
              </div>
            )
          }

          // Old format fallback
          return (
            <div key={i} className="flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-text-secondary w-4 shrink-0 text-right">
                  {i + 1}.
                </span>
                {step.indicator && step.indicator !== 'None' && (
                  <>
                    <div
                      style={{ transform: 'skewX(-8deg)', background: '#facc15' }}
                      className="rounded px-3 py-1"
                    >
                      <span
                        style={{ display: 'inline-block', transform: 'skewX(8deg)' }}
                        className="text-sm font-medium text-black font-mono"
                      >
                        {step.indicator}
                      </span>
                    </div>
                    <span className="text-text-secondary text-xs">→</span>
                  </>
                )}
                <span className="text-xs text-text-secondary italic">{step.operation}</span>
                <span className="text-text-secondary text-xs">→</span>
                <span className="px-3 py-1 rounded-full bg-blue-500 text-white text-sm font-bold tracking-widest font-mono">
                  {step.result}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Legacy letter_breakdown fallback
  if (letterBreakdown && letterBreakdown.length > 0) {
    return (
      <div className="space-y-2">
        {letterBreakdown.map((part, i) => (
          <div key={i} className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-text-secondary w-4 shrink-0 text-right">
              {i + 1}.
            </span>
            <span className="px-3 py-1 rounded-full bg-blue-500 text-white text-sm font-bold tracking-widest font-mono">
              {part.letters}
            </span>
            <span className="text-text-secondary text-xs">from</span>
            <span className="text-sm text-text">{part.source}</span>
          </div>
        ))}
      </div>
    )
  }

  return null
}

// Helper component for revealable sections
function ExplanationSection({
  title,
  revealed,
  onReveal,
  children,
}: {
  title: React.ReactNode
  revealed: boolean
  onReveal: () => void
  children: React.ReactNode
}) {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div
        className={`flex justify-between items-center px-4 py-3 ${
          revealed ? 'bg-surface' : 'bg-surface hover:bg-surface-highlight cursor-pointer'
        }`}
        onClick={!revealed ? onReveal : undefined}
      >
        <span className="font-medium text-text">{title}</span>
        {!revealed && (
          <button
            onClick={onReveal}
            className="px-3 py-1 text-sm font-medium text-primary bg-primary/10 rounded hover:bg-primary/20 transition-colors"
          >
            Reveal
          </button>
        )}
      </div>
      {revealed && <div className="px-4 py-3 border-t border-border bg-bg">{children}</div>}
    </div>
  )
}
